import {
  CourseModuleComponentItem,
  CourseModuleDropdownSelection
} from '@/store/modules/jtms/course-module/course-module.types';
import { isDescendant } from '@/utils/jtms-utils';
import { CourseModuleState } from './course-module.types';

export const courseModuleGetters = {
  /**
   * Returns a filtered list of available prerequisites for a specific module.
   *
   * This getter is crucial for preventing circular dependencies. It excludes:
   * 1. The module itself.
   * 2. Any module that already lists the target module in its own `prerequisiteIds` list.
   *
   * @param state - The Vuex state.
   * @returns A function that takes a module's tempId and returns an array of
   *          valid CourseModuleComponentItem prerequisites.
   */
  getAvailablePrerequisitesMap(
    state: CourseModuleState
  ): {
    [key: number]: CourseModuleComponentItem[];
  } {
    const allCourseModules = state.courseModulesTabState.activeCourseModules;

    return allCourseModules.reduce((map, targetModule) => {
      const targetModuleTempId = targetModule.tempId!;
      // Find the available prerequisites for the current targetModule
      const availablePrereqs = allCourseModules.filter((potentialPrereq) => {
        const potentialPrereqTempId = potentialPrereq.tempId!;

        // Rule 1: A module cannot be its own prerequisite.
        if (potentialPrereqTempId === targetModuleTempId) {
          return false;
        }

        // Rule 2 (Advanced Circular Check):
        // A module ('potentialPrereq') is NOT a valid prerequisite for 'targetModule'
        // if 'targetModule' is already a descendant of 'potentialPrereq'.
        // In other words, check if a path already exists from potentialPrereq -> ... -> targetModule.
        // If such a path exists, adding targetModule -> potentialPrereq would create a cycle.
        if (
          isDescendant(
            targetModuleTempId,
            potentialPrereqTempId,
            allCourseModules
          )
        ) {
          return false;
        }

        // If it passes all checks, it's a valid option.
        return true;
      });

      map[targetModuleTempId] = availablePrereqs;
      return map;
    }, {} as { [key: number]: CourseModuleComponentItem[] });
  },

  /**
   * Returns the active course modules formatted for use in a selection
   * dropdown in packages component.
   */
  activeCourseModulesForDropdown(
    state: CourseModuleState
  ): CourseModuleDropdownSelection[] {
    const activeModules = state.courseModulesTabState.activeCourseModules;

    // Early return if undefined OR no active course modules
    if (!activeModules || activeModules.length === 0) {
      return [];
    }

    return activeModules.map((module) => ({
      id: module.id!,
      label: module.label!
    }));
  }
};
